import type { ErrorEvent } from "@sentry/react";

import { normalizeSegment } from "./http-client-fingerprint";

// `assertQuery` throws one `QueryStatusAssertionError` with the same message for
// every call site, so Sentry groups it on the stacktrace: the route component
// and the api module are part of the key, and a release of minified chunks
// shifts the anonymous frames again. 14 days of platform-lcm produced 32 issues
// for 102 events.
//
// Measured over those 102 events: 21 are a 401 `{"message":"Unauthorized"}`
// spread over 18 different issues and 11 endpoints — one expired session, not 18
// bugs — and 12 more are `ERR_NETWORK` (offline laptop, tunnel down). Neither is
// endpoint-specific, so keying them on the endpoint keeps the fragmentation and
// only re-labels it: those two get one bucket each, whatever the query was.
// Everything left is a real per-endpoint failure and stays keyed on the request.
//
// Returns null for anything that is not a query-status assertion, leaving
// Sentry's default grouping alone.
export function getQueryStatusAssertionFingerprint(
  event: ErrorEvent,
): Array<string> | null {
  if (!isQueryStatusAssertionEvent(event)) {
    return null;
  }
  const context = getAssertionContext(event);
  if (!context) {
    return null;
  }
  if (
    context.responseStatus !== undefined &&
    AUTH_STATUSES.has(context.responseStatus)
  ) {
    return [ERROR_NAME, "auth", `status:${context.responseStatus}`];
  }
  // Amplify's NoSignedUser and react-shared's NoSessionFoundError never get
  // here: beforeSend drops them first (see session-lost-event.ts).
  const cause = getCause(event);
  if (
    context.code === NETWORK_ERROR_CODE ||
    (cause?.value !== undefined && NETWORK_CAUSE_MESSAGE.test(cause.value))
  ) {
    return [ERROR_NAME, "network"];
  }
  const endpoint = context.url ? getEndpointShape(context.url) : null;
  if (endpoint) {
    return [
      ERROR_NAME,
      context.actualStatus,
      endpoint,
      `status:${context.responseStatus ?? "none"}`,
    ];
  }
  return getCauseFallbackFingerprint(event, context);
}

// Hand-rolled `useQuery` hooks (the amplify-backed DCO queries) expose neither
// `errorPayload` nor a `queryKey` on the result, so there is no endpoint to key
// on — these were the events left fragmenting one issue per release. The cause
// error's type plus the route pattern is stable across releases and still
// separates genuinely different failures; without either there is nothing
// better than Sentry's default grouping.
function getCauseFallbackFingerprint(
  event: ErrorEvent,
  context: AssertionContext,
): Array<string> | null {
  const causeType = getCause(event)?.type;
  const transaction = event.transaction;
  if (!causeType && !transaction) {
    return null;
  }
  return [
    ERROR_NAME,
    context.actualStatus,
    `cause:${causeType ?? "none"}`,
    transaction ?? "unknown-route",
  ];
}

// The session expired: axios is configured `doNotAuthRetry` on these queries, so
// the render crashes instead of refreshing. One issue for every expired session
// is what makes the count actionable — still reported, just not once per tab.
const AUTH_STATUSES = new Set([401, 403]);

// No response at all: the browser lost the network, or a local tunnel is down.
const NETWORK_ERROR_CODE = "ERR_NETWORK";

// Non-axios fetchers word the same failure differently: Amplify's GraphQL
// client says "A network error has occurred.", a bare fetch "Failed to fetch"
// (Safari: "Load failed"). One offline laptop, one bucket.
const NETWORK_CAUSE_MESSAGE =
  /^(A network error has occurred|Network Error|Failed to fetch|Load failed|NetworkError when attempting to fetch resource)/i;

const ERROR_NAME = "QueryStatusAssertionError";

// An ErrorBoundary rethrow adds a third exception titled "React ErrorBoundary
// QueryStatusAssertionError: …", so the exception *type* is matched rather than
// the value: the boundary wrapper must not be what identifies the event.
function isQueryStatusAssertionEvent(event: ErrorEvent): boolean {
  return Boolean(
    event.exception?.values?.some((value) => value.type === ERROR_NAME),
  );
}

// Sentry unrolls the error's cause chain into `exception.values`, so the cause
// is the entry that is neither the assertion error itself nor the
// ErrorBoundary rethrow wrapping it.
function getCause(
  event: ErrorEvent,
): { type: string; value: string | undefined } | undefined {
  const cause = event.exception?.values?.find(
    (value) =>
      typeof value.type === "string" &&
      value.type !== ERROR_NAME &&
      !value.type.startsWith("React ErrorBoundary"),
  );
  if (!cause?.type) {
    return undefined;
  }
  return { type: cause.type, value: cause.value };
}

type AssertionContext = {
  actualStatus: string;
  url: string | undefined;
  responseStatus: number | undefined;
  code: string | undefined;
};

// `extraErrorDataIntegration` copies the error's own properties into
// `contexts[error.name]`. `errorPayload` only exists when the query failed on an
// axios error; a `pending` assertion has none, and its `queryKey` first element
// is the endpoint (orval builds the key from the request path).
function getAssertionContext(event: ErrorEvent): AssertionContext | null {
  const context = event.contexts?.[ERROR_NAME];
  if (typeof context !== "object" || context === null) {
    return null;
  }
  const actualStatus =
    "actualStatus" in context && typeof context.actualStatus === "string"
      ? context.actualStatus
      : "unknown";
  const payload = getErrorPayload(context);
  return {
    actualStatus,
    url: payload?.url ?? getQueryKeyUrl(context),
    responseStatus: payload?.status,
    code: payload?.code,
  };
}

function getErrorPayload(context: object): {
  url: string | undefined;
  status: number | undefined;
  code: string | undefined;
} | null {
  if (!("errorPayload" in context)) {
    return null;
  }
  const { errorPayload } = context;
  if (typeof errorPayload !== "object" || errorPayload === null) {
    return null;
  }
  const url =
    "url" in errorPayload && typeof errorPayload.url === "string"
      ? errorPayload.url
      : undefined;
  const status =
    "status" in errorPayload && typeof errorPayload.status === "number"
      ? errorPayload.status
      : undefined;
  const code =
    "code" in errorPayload && typeof errorPayload.code === "string"
      ? errorPayload.code
      : undefined;
  return { url, status, code };
}

function getQueryKeyUrl(context: object): string | undefined {
  if (!("queryKey" in context) || !Array.isArray(context.queryKey)) {
    return undefined;
  }
  const [first] = context.queryKey;
  return typeof first === "string" ? first : undefined;
}

// The url here is the axios request url — relative for our clients — so it is
// normalized as a bare path instead of going through `new URL`. Identifiers are
// collapsed so one endpoint stays one issue across campaigns; a query string is
// dropped entirely, since the assertion is about the endpoint failing and the
// http-client issue already separates filters.
function getEndpointShape(url: string): string | null {
  const [path] = url.split("?");
  if (!path) {
    return null;
  }
  return path.split("/").map(normalizeSegment).join("/");
}
